import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardYellow.css';
import './ContentCreatorDashboard.css';
import '../../styles/modals.css';
import DashboardCard from '../../components/dashboard/DashboardCard';
import { FaTimes, FaUpload, FaSpinner, FaPlus, FaSave } from 'react-icons/fa';
import API_CONFIG from '../../config/api';
import axios from "axios";
import { useAuth } from '../../contexts/AuthContext';

const ContentCreatorDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    // State management
    const [userPage, setUserPage] = useState(null);
    const [userType, setUserType] = useState(null);
    const [categories, setCategories] = useState([]);
    const [showCreatePageModal, setShowCreatePageModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [coverImageFile, setCoverImageFile] = useState(null);
    const [coverImagePreview, setCoverImagePreview] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false); // <-- Success state

    // Form data
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        slug: '',
        cover_image_url: '',
        category_id: '',
        working_style: '',
        page_goals: '',
        supporting_information: '',
        ever_sued: 'no'
    });

    // Generate slug from title
    useEffect(() => {
        if (formData.title && !formData.slug) {
            const generatedSlug = formData.title
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
            setFormData(prev => ({ ...prev, slug: generatedSlug }));
        }
    }, [formData.title, formData.slug]);

    // Load user's page and categories on mount
    useEffect(() => {
        // load data once (or when user changes)
        loadUserPage();
        loadCategories();

        // inline fetchUserType logic so eslint doesn't warn about missing dependency
        const applyUserType = () => {
            try {
                if (user && user.user_type !== 'super_admin') {
                    // If you intended to log something, log actual value
                    console.log(user.user_type);
                }
                setUserType(user?.user_type || null);
            } catch (err) {
                console.error('Error fetching user type:', err);
            }
        };

        applyUserType();
    }, [user]); // re-run when `user` changes

    // Load user's page
    const loadUserPage = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/v1/pages/me`);
            const data = response.data;
            setUserPage(data);
            sessionStorage.setItem('page_id', data.id);
        } catch (err) {
            console.error('Error loading user page:', err);
            if (err.response && err.response.status === 404) {
                setUserPage(null);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await axios.get(`${API_CONFIG.API_URL}/categories/categories/?page=1&page_size=20`);
            const data = response.data;
            if (data.categories && Array.isArray(data.categories)) {
                setCategories(data.categories);
            } else if (Array.isArray(data)) {
                setCategories(data);
            } else {
                setCategories([]);
            }
        } catch (err) {
            console.error('Error loading categories:', err);
            setCategories([]);
        }
    };

    // Handle cover image upload
    const handleCoverImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCoverImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setCoverImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Upload cover image
    const uploadCoverImage = async () => {
        if (!coverImageFile) return null;

        const uploadFormData = new FormData();
        uploadFormData.append('file', coverImageFile);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/pages/upload/image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: uploadFormData
            });

            if (response.ok) {
                const data = await response.json();
                return data.file_url;
            } else {
                const errorData = await response.json();
                console.error('Upload error:', errorData);
                setError('Failed to upload cover image');
                return null;
            }
        } catch (err) {
            console.error('Error uploading cover image:', err);
            setError('Error uploading cover image');
            return null;
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            slug: '',
            cover_image_url: '',
            category_id: '',
            working_style: '',
            page_goals: '',
            supporting_information: '',
            ever_sued: 'no'
        });
        setCoverImageFile(null);
        setCoverImagePreview('');
        setError('');
    };

    // Open modal for creating new page
    const openCreatePageModal = () => {
        setIsEditMode(false);
        setIsSubmitted(false);
        resetForm();
        setShowCreatePageModal(true);
        document.body.style.overflow = '';
    };

    // Open modal for editing
    const openEditPageModal = () => {
        if (userPage) {
            setFormData({
                title: userPage.title || '',
                description: userPage.description || '',
                slug: userPage.slug || '',
                cover_image_url: userPage.cover_image_url || '',
                working_style: userPage.working_style || '',
                page_goals: userPage.page_goals || '',
                supporting_information: userPage.supporting_information || '',
                ever_sued: userPage.ever_sued === 'yes' ? 1 : 0, // convert boolean to integer
                category_id: userPage.category_id || ''
            });
            // Set cover image preview from existing cover_image_url
            if (userPage.cover_image_url) {
                const imageUrl = userPage.cover_image_url.startsWith('http') 
                    ? userPage.cover_image_url 
                    : `${API_CONFIG.BASE_URL}${userPage.cover_image_url}`;
                setCoverImagePreview(imageUrl);
            } else {
                setCoverImagePreview('');
            }
            setIsEditMode(true);
            setIsSubmitted(false);
            setShowCreatePageModal(true);
            document.body.style.overflow = '';
        }
    };

    // Close modal
    const closeModal = () => {
        setShowCreatePageModal(false);
        setIsEditMode(false);
        setIsSubmitted(false);
        resetForm();
        document.body.style.overflow = '';
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            let coverImageUrl = formData.cover_image_url;

            if (coverImageFile) {
                const uploadedUrl = await uploadCoverImage();
                if (uploadedUrl) {
                    coverImageUrl = uploadedUrl;
                } else {
                    setIsSubmitting(false);
                    return;
                }
            }

            const pageData = {
                title: formData.title,
                category_id: parseInt(formData.category_id),
                description: formData.description || '',
                slug: formData.slug || '',
                cover_image_url: coverImageUrl || '',
                working_style: formData.working_style || '',
                page_goals: formData.page_goals || '',
                supporting_information: formData.supporting_information || '',
                ever_sued: formData.ever_sued === 'yes' ? 1 : 0, // convert boolean to integer
            };

            const token = localStorage.getItem('token');
            let response;

            if (isEditMode && userPage) {
                response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/pages/${userPage.id}`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(pageData)
                });
            } else {
                response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/pages/`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(pageData)
                });
            }

            if (response.ok) {
                const pageResponse = await response.json();
                setUserPage(pageResponse);
                setIsEditMode(false);
                setIsSubmitted(true); // <-- show success message
                resetForm();
            } else if (response.status === 422) {
                // Handle validation errors
                const errorData = await response.json();
                const messages = [];

                if (errorData.detail && Array.isArray(errorData.detail)) {
                    // Pydantic returns an array of errors
                    errorData.detail.forEach(err => {
                        if (err.msg) {
                            messages.push(err.msg);
                        }
                    });
                } else if (errorData.detail && typeof errorData.detail === 'string') {
                    messages.push(errorData.detail);
                }

                setError(messages);
            } else {
                const errorData = await response.json();
                setError([errorData.detail || `Failed to ${isEditMode ? 'update' : 'create'} page`]);
            }
        } catch (err) {
            setError(`An error occurred while ${isEditMode ? 'updating' : 'creating'} the page`);
            console.error('Error submitting page:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="dashboard-container">
                <div className="loading-container">
                    <FaSpinner className="spinner" />
                    <p>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <div className="dashboard-title">
                <h1>Content Creator Dashboard</h1>
            </div>

            {!userPage && (
                <div className="page-status-banner">
                    <div className="banner-content">
                        <h3>No Page Yet</h3>
                        <p>Before you can start creating posts, we need to verify.</p>
                    </div>
                </div>
            )}

            <div className="dashboard-cards-row">
                {userPage && (
                    <>
                        <div className="dashboard-card-container">
                            <DashboardCard
                                title="My Page"
                                description="Create and manage your page. Set up your profile, cover image, and page details."
                                icon="📄"
                                buttonText={userPage ? "Edit Page" : "Create Page"}
                                buttonAction={userPage ? openEditPageModal : openCreatePageModal}
                                index={0}
                            />
                        </div>
                        <div className="dashboard-card-container">
                            <DashboardCard
                                title="My Posts"
                                description="Create, edit, and organize your content with our enhanced content management system."
                                icon="📝"
                                buttonText={userPage.is_approved ? "Manage Posts" : "Create Page First"}
                                buttonAction={userPage.is_approved ? () => navigate('/manage-content') : openCreatePageModal}
                                index={1}
                            />
                        </div>
                    </>
                )}

                {!userPage && (
                    <div className="dashboard-card-container">
                        <DashboardCard
                            title="Create New Page"
                            description="Start publishing content by creating your unique profile page."
                            icon="✨"
                            buttonText="Create Page"
                            buttonAction={openCreatePageModal}
                            index={2}
                        />
                    </div>
                )}

                {userType === 'super_admin' && (
                    <div className="dashboard-card-container">
                        <DashboardCard
                            title="View Admin Dashboard"
                            description="View and approve page requests and other admin tasks."
                            icon="✨"
                            buttonText="View Requests"
                            buttonAction={() => navigate('/dashboard/super-admin')}
                            index={3}
                        />
                    </div>
                )}
            </div>

            {showCreatePageModal && (
                <div className="modal-backdrop">
                    <div className="modal-content modal-content--large create-page-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">{isEditMode ? 'Edit Your Page' : 'Create Your Page'}</h2>
                            <button className="modal-close-button" onClick={closeModal} aria-label="Close modal">
                                ×
                            </button>
                        </div>

                        {isSubmitted ?(
                            <div className="success-message-container">
                                {/* Animated Success Icon */}
                                <div className="success-icon-wrapper">
                                    <div className="success-checkmark">
                                        <svg viewBox="0 0 52 52" className="checkmark-svg">
                                            <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
                                            <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                                        </svg>
                                    </div>
                                </div>

                                {/* Success Message */}
                                <h3 className="success-title">
                                    {userPage?.is_approved === false ? 'Under Review!' : 'Success!'}
                                </h3>
                                
                                <p className="success-message">
                                    {userPage?.is_approved === false 
                                        ? 'Your page is under review and you will receive an email once it\'s approved.' 
                                        : 'Your page has been successfully updated!'}
                                </p>

                                {/* Confetti Animation */}
                                <div className="confetti-container">
                                    {[...Array(30)].map((_, i) => (
                                        <div key={i} className="confetti" style={{
                                            '--delay': `${Math.random() * 3}s`,
                                            '--x': `${Math.random() * 100}%`,
                                            '--rotation': `${Math.random() * 360}deg`,
                                            '--color': ['#FED026', '#FFA500', '#FFD700', '#2C2C2C'][Math.floor(Math.random() * 4)]
                                        }}></div>
                                    ))}
                                </div>

                                {/* Premium Close Button */}
                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-large success-close-btn"
                                        onClick={closeModal}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                        Got it!
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="create-page-form">
                                {error && error.length > 0 && (
                                    <div className="error-message">
                                        <ul>
                                            {error.map((errMsg, idx) => (
                                                <li key={idx}>{errMsg}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}


                                {/* Category */}
                                <div className="form-group">
                                    <label htmlFor="category_id">Category *</label>
                                    <select
                                        id="category_id"
                                        name="category_id"
                                        value={formData.category_id}
                                        onChange={handleInputChange}
                                        required
                                        className="form-control"
                                    >
                                        <option value="">Select a category</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Page Title */}
                                <div className="form-group">
                                    <label htmlFor="title">Page Title *</label>
                                    <input
                                        type="text"
                                        id="title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="Enter your page title"
                                        required
                                        maxLength={200}
                                        className="form-control"
                                    />
                                </div>

                                {/* Description */}
                                <div className="form-group">
                                    <label htmlFor="description">Description</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="Describe your page"
                                        rows="3"
                                        className="form-control"
                                    />
                                </div>

                                {/* Cover Image */}
                                <div className="form-group">
                                    <label htmlFor="cover_image">Cover Image</label>
                                    <div className="image-upload-container">
                                        <input
                                            type="file"
                                            id="cover_image"
                                            accept="image/*"
                                            onChange={handleCoverImageChange}
                                            className="hidden-input"
                                            style={{ display: 'none' }}
                                        />
                                        {coverImagePreview ? (
                                            <label 
                                                htmlFor="cover_image" 
                                                className="image-preview-label"
                                                style={{ cursor: 'pointer', position: 'relative', display: 'block' }}
                                            >
                                                <div className="image-preview" style={{ position: 'relative' }}>
                                                    <img 
                                                        src={coverImagePreview} 
                                                        alt="Cover preview" 
                                                        style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px' }}
                                                    />
                                                    <div style={{ 
                                                        position: 'absolute', 
                                                        bottom: '10px', 
                                                        left: '50%', 
                                                        transform: 'translateX(-50%)',
                                                        background: 'rgba(0,0,0,0.7)',
                                                        color: 'white',
                                                        padding: '6px 12px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px'
                                                    }}>
                                                        Click to replace
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="remove-image-btn"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setCoverImageFile(null);
                                                            setCoverImagePreview('');
                                                        }}
                                                        style={{ position: 'absolute', top: '10px', right: '10px' }}
                                                    >
                                                        <FaTimes /> Remove
                                                    </button>
                                                </div>
                                            </label>
                                        ) : (
                                            <label htmlFor="cover_image" className="image-upload-label">
                                                <div className="upload-placeholder">
                                                    <FaUpload />
                                                    <p>Click to upload cover image</p>
                                                    <small>JPG, PNG, GIF (Max 5MB)</small>
                                                </div>
                                            </label>
                                        )}
                                    </div>
                                </div>

                                {/* Working Style */}
                                <div className="form-group">
                                    <label htmlFor="working_style">Working Style</label>
                                    <select
                                        id="working_style"
                                        name="working_style"
                                        value={formData.working_style || ""}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    >
                                        <option value="">Select working style</option>
                                        <option value="Alone">Alone</option>
                                        <option value="Group">Group</option>
                                        <option value="Community">Community</option>
                                    </select>
                                </div>

                                {/* Page Goals */}
                                <div className="form-group">
                                    <label htmlFor="page_goals">Page Goals</label>
                                    <textarea
                                        id="page_goals"
                                        name="page_goals"
                                        value={formData.page_goals || ""}
                                        onChange={handleInputChange}
                                        placeholder="Describe the goals of this page"
                                        rows="3"
                                        className="form-control"
                                    />
                                </div>

                                {/* Ever Sued */}
                                <div className="form-group">
                                    <label htmlFor="sued">Have you ever been sued?</label>
                                    <div className="radio-group">
                                        <label>
                                            <input
                                                type="radio"
                                                name="ever_sued"
                                                value="yes"
                                                checked={formData.ever_sued === 1?'yes':''}
                                                onChange={handleInputChange}
                                            />
                                            Yes
                                        </label>
                                        <label>
                                            <input
                                                type="radio"
                                                name="ever_sued"
                                                value="no"
                                                checked={formData.ever_sued === 0? 'no':''}
                                                onChange={handleInputChange}
                                            />
                                            No
                                        </label>
                                    </div>
                                </div>

                                {/* Supporting Information (conditionally rendered) */}
                                {formData.ever_sued === 'yes' && (
                                    <div className="form-group">
                                        <label htmlFor="supporting_information">Supporting Information</label>
                                        <textarea
                                            id="supporting_information"
                                            name="supporting_information"
                                            value={formData.supporting_information || ""}
                                            onChange={handleInputChange}
                                            placeholder="Provide supporting information"
                                            rows="3"
                                            className="form-control"
                                        />
                                    </div>
                                )}


                                {/* Form Actions */}
                                <div className="form-actions">
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-large"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <FaSpinner className="spinner" /> {isEditMode ? 'Updating...' : 'Creating...'}
                                            </>
                                        ) : (
                                            <>
                                                {isEditMode ? <FaSave /> : <FaPlus />} {isEditMode ? 'Update Page' : 'Create Page'}
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeModal}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContentCreatorDashboard;
